import { useState } from "react";
import { NotificationLogList } from "./components/NotificationLogList";
import { Loading } from "@/components/ui/loading";
import { useNotificationLogs } from "./hooks/useNotificationLogs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Filter, Calendar, X } from "lucide-react";
import { cn } from "@/lib/utils";

export default function ManageNotificationLogs() {
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const {
    // Data and pagination
    notificationLogs,
    isLoading,
    stats,
    totalPages,
    currentPage,
    totalElements,
    pageSize,
    handlePageChange,

    // Sorting
    sortField,
    sortDirection,
    handleSort,

    // Filtering
    search,
    setSearch,
    selectedStatuses,
    setSelectedStatuses,
    selectedChannels,
    setSelectedChannels,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,

    // Actions
    handleResend,
    handleCancel,

    // Filter options
    statuses,
    channels,
  } = useNotificationLogs();

  const filterButtonClass =
    "transition-colors px-3 py-1 text-xs font-medium rounded-full border";

  const statusOptions = [
    { value: "PENDING", label: "Pending" },
    { value: "SENT", label: "Sent" },
    { value: "FAILED", label: "Failed" },
    { value: "CANCELLED", label: "Cancelled" },
  ];

  const channelOptions = [
    { value: "EMAIL", label: "Email" },
    { value: "SMS", label: "SMS" },
  ];

  const clearAllFilters = () => {
    setSelectedStatuses([]);
    setSelectedChannels([]);
    setDateFrom("");
    setDateTo("");
    setSearch("");
  };

  const hasActiveFilters =
    selectedStatuses.length > 0 ||
    selectedChannels.length > 0 ||
    dateFrom ||
    dateTo ||
    search;

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            Notification Logs
          </h1>
          <p className="text-muted-foreground">
            View and manage notification delivery logs
          </p>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-warning">
                {stats.pending}
              </div>
              <div className="text-sm text-muted-foreground">Pending</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-success">
                {stats.sent}
              </div>
              <div className="text-sm text-muted-foreground">Sent</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-destructive">
                {stats.failed}
              </div>
              <div className="text-sm text-muted-foreground">Failed</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-muted-foreground">
                {stats.cancelled}
              </div>
              <div className="text-sm text-muted-foreground">Cancelled</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="flex-1">
          <Input
            placeholder="Search notifications..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm"
          />
        </div>

        {/* Advanced Filters */}
        <Popover
          open={showAdvancedFilters}
          onOpenChange={setShowAdvancedFilters}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="font-semibold px-6 py-2 rounded-lg border"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-2">
                  {(() => {
                    const activeFilters = [];
                    if (selectedStatuses.length > 0) {
                      activeFilters.push(`${selectedStatuses.length} status`);
                    }
                    if (selectedChannels.length > 0) {
                      activeFilters.push(`${selectedChannels.length} channel`);
                    }
                    if (dateFrom || dateTo) {
                      activeFilters.push("date range");
                    }
                    return activeFilters.join(", ");
                  })()}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[400px] p-5 space-y-6 bg-card border border-border rounded-lg shadow-lg">
            {/* Status Multi-Select */}
            <div>
              <span className="block font-semibold text-foreground mb-2">
                Status
              </span>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant={
                    selectedStatuses.length === 0 ? "default" : "outline"
                  }
                  onClick={() => setSelectedStatuses([])}
                  className={cn(
                    filterButtonClass,
                    selectedStatuses.length === 0
                      ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                      : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  All
                </Button>
                {statusOptions.map((status) => (
                  <Button
                    key={status.value}
                    size="sm"
                    variant={
                      selectedStatuses.includes(status.value)
                        ? "default"
                        : "outline"
                    }
                    onClick={() => {
                      setSelectedStatuses((prev) =>
                        prev.includes(status.value)
                          ? prev.filter((s) => s !== status.value)
                          : [...prev, status.value],
                      );
                    }}
                    className={cn(
                      filterButtonClass,
                      selectedStatuses.includes(status.value)
                        ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                        : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                    )}
                  >
                    {status.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Channel Multi-Select */}
            <div>
              <span className="block font-semibold text-foreground mb-2">
                Channel
              </span>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant={
                    selectedChannels.length === 0 ? "default" : "outline"
                  }
                  onClick={() => setSelectedChannels([])}
                  className={cn(
                    filterButtonClass,
                    selectedChannels.length === 0
                      ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                      : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  All
                </Button>
                {channelOptions.map((channel) => (
                  <Button
                    key={channel.value}
                    size="sm"
                    variant={
                      selectedChannels.includes(channel.value)
                        ? "default"
                        : "outline"
                    }
                    onClick={() => {
                      setSelectedChannels((prev) =>
                        prev.includes(channel.value)
                          ? prev.filter((c) => c !== channel.value)
                          : [...prev, channel.value],
                      );
                    }}
                    className={cn(
                      filterButtonClass,
                      selectedChannels.includes(channel.value)
                        ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                        : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                    )}
                  >
                    {channel.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Date Range */}
            <div>
              <span className="block font-semibold text-foreground mb-2">
                <Calendar className="h-4 w-4 inline mr-2" />
                Date Range
              </span>
              <div className="space-y-2">
                <Input
                  type="date"
                  placeholder="From date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
                <Input
                  type="date"
                  placeholder="To date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
            </div>

            {/* Clear All Filters */}
            {hasActiveFilters && (
              <div className="pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearAllFilters}
                  className="w-full"
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear All Filters
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>

      {/* Notification Logs Table */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loading size="lg" text="Loading notification logs..." />
        </div>
      ) : (
        <NotificationLogList
          logs={notificationLogs}
          totalPages={totalPages}
          currentPage={currentPage}
          totalElements={totalElements}
          pageSize={pageSize}
          isLoading={isLoading}
          onResend={handleResend}
          onCancel={handleCancel}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
}
